/**
 * /cells — library of cell definitions.
 *
 * A cell carries the station layout (robot + positioner placement +
 * work-object + torch) that IK, collision and RAPID all consume. A
 * project picks a cell by id; batch generation runs against that cell's
 * geometry instead of the hardcoded `CellConfig()` default that bit us
 * during RobotStudio validation.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Callout,
  Card,
  Dialog,
  DialogBody,
  DialogFooter,
  FormGroup,
  HTMLTable,
  NonIdealState,
  NumericInput,
  Spinner,
  Tag,
} from '@blueprintjs/core'

import { createCell, listCells, type CellDefinition } from '../api'

export function CellsList() {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'ok'; items: CellDefinition[] }
    | { kind: 'error'; message: string }
  >({ kind: 'loading' })
  const [dialogOpen, setDialogOpen] = useState(false)

  const refresh = async () => {
    try {
      setState({ kind: 'ok', items: await listCells() })
    } catch (err) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : String(err) })
    }
  }
  useEffect(() => { refresh() }, [])

  return (
    <div className="projects-root">
      <header className="projects-header">
        <Link to="/" className="projects-back">← Workspace</Link>
        <h2>Cells</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Link to="/projects"><Button icon="folder-close" minimal>Projects</Button></Link>
          <Button icon="add" intent="primary" onClick={() => setDialogOpen(true)}>
            New cell
          </Button>
        </div>
      </header>

      {state.kind === 'loading' && <Spinner />}
      {state.kind === 'error' && (
        <Callout intent="danger" icon="error">{state.message}</Callout>
      )}
      {state.kind === 'ok' && state.items.length === 0 && (
        <NonIdealState
          icon="build"
          title="No cells yet"
          description="A cell is a station layout — robot, positioner, work object, torch — that drives IK, collision and RAPID emit for every project that uses it."
          action={<Button icon="add" intent="primary" onClick={() => setDialogOpen(true)}>Create one</Button>}
        />
      )}
      {state.kind === 'ok' && state.items.length > 0 && (
        <Card>
          <HTMLTable striped interactive>
            <thead>
              <tr>
                <th>Name</th>
                <th>Robot</th>
                <th>Positioner</th>
                <th>Mech unit</th>
                <th>Worm origin (RCF, mm)</th>
                <th>Active tool</th>
                <th>Ver</th>
              </tr>
            </thead>
            <tbody>
              {state.items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/cells/${c.id}`}>{c.name}</Link>
                    {c.description && (
                      <div className="projects-dim" style={{ marginTop: 2 }}>
                        {c.description.slice(0, 90)}
                      </div>
                    )}
                  </td>
                  <td><Tag minimal>{c.robot.robot_id}</Tag></td>
                  <td><Tag minimal>{c.positioner.model_id}</Tag></td>
                  <td><code>{c.positioner.mech_unit_name}</code></td>
                  <td className="projects-dim">
                    ({c.positioner.base_position_mm[0]}, {c.positioner.base_position_mm[1]}, {c.positioner.base_position_mm[2] + c.positioner.faceplate_offset_mm[2]})
                  </td>
                  <td>{c.active_tool_id ?? '—'}</td>
                  <td className="projects-dim">v{c.version}</td>
                </tr>
              ))}
            </tbody>
          </HTMLTable>
        </Card>
      )}

      <NewCellDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => { setDialogOpen(false); refresh() }}
      />
    </div>
  )
}


function NewCellDialog({
  isOpen, onClose, onCreated,
}: {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [positionerX, setPositionerX] = useState(1600)
  const [positionerY, setPositionerY] = useState(0)
  const [faceplateZ, setFaceplateZ] = useState(1100)
  const [mechUnit, setMechUnit] = useState('STN1')
  const [activeToolId, setActiveToolId] = useState('panasonic_ATU01042')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async () => {
    setErr(null)
    setBusy(true)
    try {
      await createCell({
        name,
        description,
        positioner: {
          mech_unit_name: mechUnit,
          base_position_mm: [positionerX, positionerY, 0],
          faceplate_offset_mm: [0, 0, faceplateZ],
        },
        active_tool_id: activeToolId || null,
      })
      setName('')
      onCreated()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : String(ex))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="New cell" icon="build" style={{ width: 540 }}>
      <DialogBody>
        <FormGroup label="Name">
          <input className="bp5-input bp5-fill" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. SORS_SaintJohn_cell" />
        </FormGroup>
        <FormGroup label="Description" helperText="One line about where this cell lives / what makes it specific.">
          <input className="bp5-input bp5-fill" value={description} onChange={(e) => setDescription(e.target.value)} />
        </FormGroup>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <FormGroup label="Positioner X (mm)" helperText="Distance from robot base forward">
            <NumericInput value={positionerX} onValueChange={(v) => setPositionerX(v)} stepSize={50} />
          </FormGroup>
          <FormGroup label="Positioner Y (mm)">
            <NumericInput value={positionerY} onValueChange={(v) => setPositionerY(v)} stepSize={50} />
          </FormGroup>
          <FormGroup label="Faceplate Z above base (mm)" helperText="IRBP A-250 D1000 ≈ 1100">
            <NumericInput value={faceplateZ} onValueChange={(v) => setFaceplateZ(v)} stepSize={50} />
          </FormGroup>
          <FormGroup label="Positioner mech unit">
            <input className="bp5-input bp5-fill" value={mechUnit} onChange={(e) => setMechUnit(e.target.value)} />
          </FormGroup>
        </div>
        <FormGroup label="Active tool id" helperText="Tool library id — e.g. panasonic_ATU01042. Leave blank to pick per-project.">
          <input className="bp5-input bp5-fill" value={activeToolId} onChange={(e) => setActiveToolId(e.target.value)} />
        </FormGroup>
        {err && <Callout intent="danger" icon="error">{err}</Callout>}
      </DialogBody>
      <DialogFooter
        actions={
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button intent="primary" onClick={submit} loading={busy} disabled={!name || busy}>
              Create
            </Button>
          </>
        }
      />
    </Dialog>
  )
}
